import React, { forwardRef, useImperativeHandle, useCallback, useState, useEffect, useRef } from "react";
import { Background, Controls, addEdge, useNodesState, applyNodeChanges, applyEdgeChanges, useEdgesState, ReactFlowProvider, ReactFlow } from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import Typography from 'apollo-react/components/Typography';
import BuilderStagesModal from './builder_stages_modal';
// import Close from 'apollo-react-icons/Close';
// import ThumbsUpFilled from 'apollo-react-icons/ThumbsUpFilled';
// import Redo from 'apollo-react-icons/Redo';
// import IconButton from 'apollo-react/components/IconButton';
// import { toast } from "react-toastify";

const defaultViewport = { x: 0, y: 0, zoom: 1 };

const WorkflowBuilder = forwardRef(({approvalTypes, sentNodes = [], sentEdges = []}, ref) => {
  const [reactFlowInstance, setReactFlowInstance] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [lastNodeId, setLastNodeId] = useState(null);
  const deletedNodeIds = useRef(new Set());
  const [openStageFormModal, setOpenStageFormModal] = useState({ open: false, nodeStageId: null, content: null, selectedCategory: null });

  const [nodes, setNodes] = useNodesState([]);
  const [edges, setEdges] = useEdgesState([]);

  useEffect(() => {
    if (sentNodes && sentNodes.length > 0) {
      setNodes(sentNodes);
    }
  }, [sentNodes, setNodes]);

  useEffect(() => {
    if (sentEdges) {
      setEdges(sentEdges);
    }
  }, [sentEdges, setEdges]);

  let clickTimeout; // Handle click/Double Click events timeout

  // Expose the resetWorkflow method to the parent via ref if called from discard button
  useImperativeHandle(ref, () => ({
    resetWorkflow: () => {
      setNodes([
        {
          id: "start",
          type: "input",
          data: { 
            label: (<Typography variant="subtitle1">Start Flow</Typography>), 
            name: "Start Flow",
            category: 'Start' // Store in data for React Flow compatibility
          },
          position: { x: 350, y: 150 },
          category: 'Start',
        }
      ]); // Clear all nodes
      setEdges([]); // Clear all edges
      // console.log('Workflow has been reset');
    },
    getWorkflowJsonData: () => {
      return {
        nodes,
        edges,
      };
    }, // Example method to get workflow data
  }));

  const onDrop = useCallback(
    (event) => {
      event.preventDefault();
      const type = event.dataTransfer.getData('application/reactflow');
      
      const position = reactFlowInstance.screenToFlowPosition({
        x: event.clientX,
        y: event.clientY,
      });

      // Inline: Find first Submission node with target_category_id
      const getNodeCategory = (node) => node.category || node.data?.category;
      const submissionNode = nodes.find( (node) => getNodeCategory(node) === "Submission" );
      if (submissionNode) {
        setSelectedCategory(submissionNode.data.formConfig.target_category_id);
        // console.log('onDrop selectedCategory : ' + submissionNode.data.formConfig.target_category_id);
      } else {
        setSelectedCategory(null);
      }

      const nodeId = `${type}-${nodes.length}`;

      let newNode = {};

      if (type === "FormStage") {
        newNode = {
          id: nodeId,
          type: 'FormStage',
          position,
          category: 'Stage',
          data: { 
            label: <Typography variant="subtitle1">Stage</Typography>, 
            name: "Stage", 
            id: null, 
            formConfig: null,
            category: 'Stage' // Store in data for React Flow compatibility
          },
        };
        // console.log('OnDrop setOpenStageFormModal newNode:', nodeId);
        setOpenStageFormModal({ open: true, nodeStageId: nodeId, content: null, selectedCategory: selectedCategory });

      } else { // End Stage
        newNode = {
          id: `output-${nodeId}`,
          type: 'output',
          position,
          category: 'End',
          data: { 
            label: <Typography variant="subtitle1">End Flow</Typography>, 
            name: "End Flow", 
            isEndNode: true,
            category: 'End' // Store in data for React Flow compatibility
          },
        };
      }
      
      setNodes((nds) => [...nds, newNode]);

      // Add a new edge automatically if parent is start node
      if (lastNodeId !== nodeId && nodes.length === 1) {
        const newEdge = {
          id: `edge-${nodeId}`,
          source: 'start',
          target: nodeId,
          type: 'smoothstep',
          style: { stroke: '#2f80ed', strokeWidth: 2 },
        };
        setEdges((eds) => [...eds, newEdge]);
      }

      // Create an edge from the last node to the new node
      // if (lastNodeId !== nodeId) {
      //   const newEdge = {
      //     id: `edge-${nodeId}`,
      //     source: (nodes.length === 1)? 'start' : lastNodeId,
      //     target: nodeId,
      //     type: 'smoothstep',
      //     style: { stroke: '#2f80ed', strokeWidth: 2 },
      //   };
      //   setEdges((eds) => [...eds, newEdge]);
      // }

      setLastNodeId(nodeId);

    },
    [lastNodeId, reactFlowInstance, setEdges, setNodes, nodes]
  );
  
  const applyFormOnNode = (nodeStageId, jsonContent) => {
    // Confirm closing the modal
    if (openStageFormModal.open) {
      setOpenStageFormModal({ open: false, nodeStageId: null, content: null, selectedCategory: null });
    }
  
    // Get all child node IDs by filtering edges where the source matches the nodeId
    const childNodeIds = edges.filter((edge) => edge.source === nodeStageId).map((edge) => edge.target);

    // Find the actual child nodes using their IDs
    const childNodes = nodes.filter((node) => childNodeIds.includes(node.id));

    // get main node
    const mainNode = nodes.find((n) => n.id === nodeStageId);

    // Check if this node is already connected to any ReviewChildNode and check if create or update
    const hasReviewChildNode = edges.some(edge => {
      const targetNode = nodes.find(n => n.id === edge.target);
      return edge.source === nodeStageId && targetNode?.scope === 'ReviewChildNode';
    });

    // If the node is a Review stage, check if it has any child nodes, if not create them
    if (jsonContent.approver_role_id && !hasReviewChildNode) {
      const parentPosition = mainNode.position || { x: 150, y: 150 };

      // const iconMapping = {
      //   ThumbsUpFilled: ThumbsUpFilled,
      //   Close: Close,
      //   Redo: Redo,
      // };

      const newNodes = [];
      const newEdges = [];

      let childOffsetX = -383;

      approvalTypes.forEach((child) => {

          if (!childNodes.includes(child.code)) {

            // Generate Child node ID
            const childNodeId = `Approval-${nodeStageId}-${child.id}-${child.code}-${Math.floor(Math.random() * 10000)}`;

            // console.log('this childNodeId:', childNodeId);

            newNodes.push({
              id: childNodeId,
              type: `approval-${child.id}`,
              scope: 'ReviewChildNode',
              category: `child-${child.code}`,
              data: {
                label: (
                  <Typography variant="subtitle1">
                     {/* <IconButton size="extraSmall" disabled>
                       {React.createElement(iconMapping[child.ico])}
                     </IconButton> */}
                    {child.name}
                  </Typography>
                ),
                approvalTypeId: child.id,
                name: child.name, // Replacing Label content
                formConfig: {}
              },
              // parentId: nodeStageId,
              className: "child-node",
              position: { x: parentPosition.x + (childOffsetX += 200), y: parentPosition.y + 90 },
            });

            newEdges.push({
              id: `edge-${nodeStageId}-${childNodeId}-${Math.floor(Math.random() * 10000)}`,
              source: nodeStageId,
              target: childNodeId,
              type: 'smoothstep',
              style: { stroke: '#2f80ed', strokeWidth: 2 },
            });

            // console.log('newEdges:', newEdges);
          }
      });

      setNodes((prevNodes) => [...prevNodes, ...newNodes]);
      setEdges((prevEdges) => [...prevEdges, ...newEdges]);
    } else if (jsonContent.target_category_id) {
      // If the node is a Submission stage, check selectedCategory and and currently selectedCategory
      if (!selectedCategory) {
        // This means the user has changed the target category in first submission node
        const updatedNodes = nodes.map((thisNode) => {
          if (thisNode.category === "Submission") {
            return {
              ...thisNode,
              data: {
                ...thisNode.data,
                formConfig: {
                  ...thisNode.data.formConfig,
                  target_category_id: jsonContent.target_category_id,
                },
              },
            };
          }
          return thisNode;
        });
        setNodes(updatedNodes);
      }
    }

    // Update the node with new label and formConfig
    const updatedNode = {
      ...mainNode,
      category: jsonContent.target_category_id ? "Submission" : "Review",
      data: {
        ...mainNode.data,
        label: (
          <Typography variant="subtitle1">
            {jsonContent.name || "Unnamed Stage"}
          </Typography>
        ),
        name: jsonContent.name || "Unnamed Stage", // Replacing Label content
        formConfig: jsonContent,
      },
    };

    // console.log('updatedNode:', updatedNode);

    // push updates to node 
    setNodes((prevNodes) =>
      prevNodes.map((node) =>
        node.id === updatedNode.id ? updatedNode : node
      )
    );

    return updatedNode;
  };

  const onDragOver = useCallback((event) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
  }, []);

  const onNodesChange = useCallback(
    (changes) => setNodes((nds) => applyNodeChanges(changes, nds)),
    [setNodes]
  );

  const onEdgesChange = useCallback(
    (changes) => setEdges((eds) => applyEdgeChanges(changes, eds)),
    [setEdges]
  );

  const onConnect = useCallback(
    (params) => {
      setEdges((eds) => {
        // Check if the edge already exists
        const edgeExists = eds.some(
          (edge) => edge.source === params.source && edge.target === params.target
        );
  
        if (edgeExists) {
          console.warn('Edge already exists:', params);
          return eds; // Return existing edges without adding a duplicate
        }
  
        return addEdge(
          {
            ...params,
            type: "smoothstep",
            style: { stroke: "#2f80ed", strokeWidth: 2 },
          },
          eds
        );
      });
    },
    [setEdges]
  );

  const handleCloseFormModal = (nodeStageId) => {
    const node = nodes.find((n) => n.id === nodeStageId);

    // Check if the node has no name or form selected
    if (!node?.data?.formConfig?.name || !node?.data?.formConfig?.form_id) {
      // Delete the node and its connected edges
      setNodes((nodes) => nodes.filter((n) => n.id !== nodeStageId));
      setEdges((edges) => edges.filter((e) => e.source !== nodeStageId && e.target !== nodeStageId)
      );
    }
  };

  function haveStartParent(node, nodes, edges) {
    // Find all parent nodes connected to this node
    const parentNodes = edges
      .filter(edge => edge.target === node.id)
      .map(edge => nodes.find(n => n.id === edge.source))
      .filter(Boolean); // Remove undefined if any

    // Return true if any parent node is category Start
    return parentNodes.some(parent => parent.category === "Start");
  }

  const onNodeClick = (event, node) => {
    if (clickTimeout) {
      clearTimeout(clickTimeout); // Clear the timeout if a double-click occurs
    }

    event.stopPropagation();

    clickTimeout = setTimeout(() => {
      clickTimeout = null; // Reset the timeout

      // Check if the node has been deleted by double click
      if (deletedNodeIds.current.has(node.id)) {
        // console.log(`Node ${node.id} has been deleted. Skipping onNodeClick.`);
        return; // Exit if the node has been deleted
      }

      if (node.type !== 'FormStage') {
        return; // Do nothing if the node is not a FormStage (Submission or Review)
      }

      // Inline: Use the clicked node as submissionNode if it's a Submission, otherwise find one
      if (node.category === "Submission" && !haveStartParent(node, nodes, edges)) {
        setSelectedCategory(node.data.formConfig.target_category_id);
        // console.log('onNodeClick selectedCategory : ' + node.data.formConfig.target_category_id);
      } else {
        setSelectedCategory(null);
      }

      // Populate the formConfig data if available
      setOpenStageFormModal({
        open: true,
        nodeStageId: node.id,
        content: node.data?.formConfig || null,
        selectedCategory: selectedCategory,
      });
    }, 500); // Delay to differentiate single-click from double-click
  };

  const onNodeDoubleClick = (event, node) => {
    if (clickTimeout) {
      clearTimeout(clickTimeout); // Prevent single-click logic
      clickTimeout = null;
    }

    event.stopPropagation();

    if (node.type === 'input') {
      return; // Do nothing if the node is start node
    }

    if (node.scope && node.scope === 'ReviewChildNode') {
      // Check if the label contains "Send Back"
      const type = node?.type || '';
      // console.log('onNodeDoubleClick type:', type);
      if (!type.includes('sbk') && type!="approval-3") {
        return; // Prevent deletion
      }
    }

    // Add the node ID to the deletedNodeIds set
    deletedNodeIds.current.add(node.id);

    // If the node is a Review stage, delete all its children
    if (node.data?.formConfig?.approver_role_id) {
      // Filter out child nodes that are not children of the current node
      const childNodeIds = edges
      .filter((edge) => edge.source === node.id)
      .map((edge) => {
        const targetNode = nodes.find((n) => n.id === edge.target);
        return targetNode?.scope === 'ReviewChildNode' ? edge.target : null; // Include only if the target node has a scope
      });

      setNodes((nodes) =>
        nodes.filter((n) => n.id !== node.id && !childNodeIds.includes(n.id))
      );
      setEdges((edges) =>
        edges.filter(
          (e) => e.source !== node.id && !childNodeIds.includes(e.target)
        )
      );
    } else {
      // Otherwise, just delete the node and its edges
      setNodes((nodes) => nodes.filter((n) => n.id !== node.id));
      setEdges((edges) =>
        edges.filter((e) => e.source !== node.id && e.target !== node.id)
      );
    }
  };

  const onEdgeClick = (event, edge) => {
    event.stopPropagation();
    // if (edge.target.startsWith("Approval")) return;
    setEdges((eds) => eds.filter((e) => e.id !== edge.id));
  };

  // Function to get used submission form IDs
  const getUsedSubmissionFormIds = (currentNodeId) => {
    return nodes
      .filter(
        (node) =>
          node.category === "Submission" &&
          node.data?.formConfig?.form_id &&
          node.id !== currentNodeId // Exclude current node being edited
      )
      .map((node) => node.data.formConfig.form_id);
  };

  return (
    <div className='workflow_layout'>
      <ReactFlowProvider>
        <div className='workflow_flow_container' onDrop={onDrop} onDragOver={onDragOver}>
          <ReactFlow
            nodes={nodes}
            edges={edges}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onConnect={onConnect}
            onNodeClick={onNodeClick}
            onEdgeClick={onEdgeClick}
            onNodeDoubleClick={onNodeDoubleClick}
            onInit={setReactFlowInstance}
            defaultViewport={defaultViewport}
          >
            <Controls />
            <Background color="#99b3ec" variant="Lines" />
          </ReactFlow>
        </div>
      </ReactFlowProvider>

      {openStageFormModal.open && (
        <BuilderStagesModal
          nodes={nodes}
          nodeStageId={openStageFormModal.nodeStageId}
          content={openStageFormModal.content}
          node={openStageFormModal.node}
          onEvent={setOpenStageFormModal}
          onConfirm={applyFormOnNode}
          onDiscard={handleCloseFormModal}
          selectedCategory={selectedCategory}
          usedSubmissionFormIds={getUsedSubmissionFormIds(openStageFormModal.nodeStageId)}
        />
      )}
    </div>
  );
});

export default WorkflowBuilder;
